/**
 * Firebase Authentication Helper Functions
 */

import { Logger } from '../utils/Logger';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
  UserCredential
} from 'firebase/auth';
import { auth } from './config';

const logger = Logger.getLogger('FirebaseAuth');

/**
 * Sign up with email and password
 */
export async function signUpWithEmail(email: string, password: string): Promise<UserCredential> {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    logger.info('User signed up:', userCredential.user.email);
    return userCredential;
  } catch (error) {
    const authError = error as { code?: string; message?: string };
    logger.error('Sign up error:', authError.message);
    throw new Error(getAuthErrorMessage(authError.code ?? ''));
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email: string, password: string): Promise<UserCredential> {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    logger.info('User signed in:', userCredential.user.email);
    return userCredential;
  } catch (error) {
    const authError = error as { code?: string; message?: string };
    logger.error('Sign in error:', authError.message);
    throw new Error(getAuthErrorMessage(authError.code ?? ''));
  }
}

/**
 * Sign in with Google
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    logger.info('User signed in with Google:', userCredential.user.email);
    return userCredential;
  } catch (error) {
    const authError = error as { code?: string; message?: string };
    logger.error('Google sign in error:', authError.message);
    throw new Error(getAuthErrorMessage(authError.code ?? ''));
  }
}

/**
 * Sign out current user
 */
export async function logOut(): Promise<void> {
  try {
    await signOut(auth);
    logger.info('User signed out');
  } catch (error) {
    const authError = error as { message?: string };
    logger.error('Sign out error:', authError.message);
    throw new Error('Failed to sign out');
  }
}

/**
 * Get current user
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Listen to auth state changes
 */
export function onAuthChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return auth.currentUser !== null;
}

/**
 * Get user-friendly error messages
 */
function getAuthErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in instead.';
    case 'auth/invalid-email':
      return 'Invalid email address format.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed. Please try again.';
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled.';
    default:
      return 'Authentication failed. Please try again.';
  }
}

/**
 * Require authentication - redirect to login if not authenticated
 */
export function requireAuth(redirectUrl: string = '/dashboard/auth.html'): User {
  const user = getCurrentUser();
  if (!user) {
    logger.warn('User not authenticated, redirecting to login');
    window.location.href = redirectUrl;
    throw new Error('Authentication required');
  }
  return user;
}
